import { isEventShaded } from '@/features/events/lib/eventKind.lib';
import type { PageExperience } from '@/features/experiences/types';
import { useDocumentContext } from '@/features/printables/contexts/DocumentContext';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { Box, Flex } from '@/ui';
import type { DayContextType } from '@/ui/Calendar/DayContext';
import { FONT_CALENDAR_HEADING } from '../../../css/customFonts';
import { getStyleById } from '../../../theme';
import { PlannerEventItem } from '../../shared/PlannerEventItem';
import type { GroupData } from '../types';
import { isWeekend, toMonthKey } from '../util.lib';
import { MobileDayCell } from './MobileDayCell';

type Props = {
  dayContext: DayContextType;
  box: GroupData | undefined;
  experience: PageExperience;
};

export const DayCell = ({ dayContext, box, experience }: Props) => {
  const { isPrintEnv } = useDocumentContext();
  const isMobile = useIsMobileBreakpoint();
  const monthKey = toMonthKey({
    month: dayContext.month,
    year: dayContext.year,
  });
  const theme = getStyleById(experience.styleId ?? undefined);
  const isCurrentMonth = dayContext.currentMonth;

  const dayMeasureKey = dayContext.currentMonth
    ? `day-${monthKey}-${dayContext.day}`
    : undefined;
  const dayHeaderMeasureKey = dayContext.currentMonth
    ? `day-header-${monthKey}-${dayContext.day}`
    : undefined;

  if (!isCurrentMonth) {
    return <Box>&nbsp;</Box>;
  }

  if (!isPrintEnv && isMobile && box) {
    return (
      <MobileDayCell
        box={box}
        dayContext={dayContext}
        experience={experience}
      />
    );
  }

  const hasBlockingEvent = box?.events.some(isEventShaded);

  const events = box?.events ?? [];

  return (
    <Flex
      border="solid 1px"
      borderColor={theme.neutral6 || theme.borderColor}
      borderRadius="0.25em"
      boxSizing="border-box"
      data-measure={dayMeasureKey}
      flexDirection="column"
      minH={isPrintEnv ? 0 : { md: '6em', lg: '7.5em' }}
      minW={0}
      overflow={isPrintEnv ? 'hidden' : 'auto'}
      w="100%"
      bg={
        (hasBlockingEvent ? theme.neutral9 : theme.neutral10) ||
        theme.backgroundColor
      }
    >
      <Box
        data-measure={dayHeaderMeasureKey}
        fontFamily={FONT_CALENDAR_HEADING}
        fontSize="1em"
        fontWeight="600"
        pb="0.25em"
        pt="0.3em"
        px="0.5em"
        textAlign="left"
        color={
          isWeekend(dayContext.dayOfWeek) ? theme.neutral5 : theme.textColor
        }
      >
        {dayContext.day}
      </Box>
      <Flex
        flexDirection="column"
        flexGrow={1}
        justifyContent="flex-start"
        overflow={isPrintEnv ? 'hidden' : 'auto'}
        p="0 0.25em 0.3em 0.5em"
        data-measure={
          dayContext.currentMonth
            ? `day-content-${monthKey}-${dayContext.day}`
            : undefined
        }
      >
        {box &&
          events.map((event, index) => {
            const key = `event-${event.id}-${box.date.toISODate()}`;
            const gap = index > 0 ? '0.2em' : undefined;
            return (
              <Box data-measure={key} key={key} mt={gap}>
                <PlannerEventItem
                  date={box.date}
                  event={event}
                  experience={experience}
                />
              </Box>
            );
          })}
      </Flex>
    </Flex>
  );
};
